<template>
  <div ref="pageContainer" class="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
    <div class="absolute inset-0 pointer-events-none">
      <img
        v-for="(bee, index) in bees"
        :key="index"
        :src="beeImage"
        alt="Bee"
        class="absolute w-12 h-12 transition-none"
        :style="{
          left: bee.x + 'px',
          top: bee.y + 'px',
          transform: `scaleX(${bee.direction})`,
        }"
      />
    </div>
    
    <div ref="contentContainer" class="max-w-md w-full space-y-8 relative z-10">
      <div class="bg-white rounded-lg shadow-lg p-8 border border-creme-300">
        <div>
          <h2 class="mt-6 text-center text-3xl font-marketing font-bold text-primary">
            Login
          </h2>
          <p class="mt-2 text-center text-sm text-gray-900 font-sans">
            Sign in to your account
          </p>
        </div>
        
        <form class="mt-8 space-y-6" @submit.prevent="handleLogin">
        <div v-if="sessionExpired && !error" class="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-md text-sm font-sans">
          Your session expired. Please sign in again to continue.
        </div>
        <div v-if="error && !shouldShowDialog" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm font-sans">
          {{ error }}
        </div>
        <div class="space-y-4">
          <div>
            <label for="email" class="block text-sm font-marketing font-medium text-primary mb-1">
              Email address
            </label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              class="appearance-none relative block w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 focus:z-10 sm:text-sm font-sans"
              placeholder="your@email.com"
            />
          </div>
          
          <div>
            <label for="password" class="block text-sm font-marketing font-medium text-primary mb-1">
              Password
            </label>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              class="appearance-none relative block w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 focus:z-10 sm:text-sm font-sans"
              placeholder="Enter your password"
            />
          </div>
        </div>

        <div>
          <button
            type="submit"
            :disabled="loading"
            class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-marketing font-medium rounded-md text-white bg-accent hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ loading ? 'Signing in...' : 'Sign in' }}
          </button>
        </div>

        <div class="text-center space-y-2">
          <p class="text-sm text-gray-900 font-sans">
            <a href="#" @click.prevent="showForgotPassword = true" class="font-marketing font-medium text-accent hover:text-green-700 transition-colors">
              Forgot password?
            </a>
          </p>
          <p class="text-sm text-gray-900 font-sans">
            Do not have an account?
            <a href="/register" class="font-marketing font-medium text-accent hover:text-green-700 transition-colors">
              Register
            </a>
          </p>
        </div>
      </form>
      </div>
    </div>

    <div v-if="shouldShowDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div class="mb-4">
          <h3 class="text-xl font-marketing font-bold mb-2">
            {{ errorTitle }}
          </h3>
          <p class="text-gray-700 font-sans">
            {{ error }}
          </p>
        </div>
        <div class="flex justify-end">
          <button
            @click="error = ''"
            class="px-4 py-2 text-sm font-marketing bg-green-700 text-white rounded-md hover:bg-green-800"
          >
            OK
          </button>
        </div>
      </div>
    </div>

    <div v-if="showForgotPassword" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div class="mb-4">
          <h3 class="text-xl font-marketing font-bold mb-2">
            Reset Password
          </h3>
          <p class="text-gray-700 font-sans mb-4">
            Enter your email address and we'll send you a link to reset your password.
          </p>
          <div v-if="forgotPasswordMessage" :class="[
            'text-sm font-sans mb-4 px-4 py-3 rounded-md',
            forgotPasswordMessage.includes('sent') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          ]">
            {{ forgotPasswordMessage }}
          </div>
          <div>
            <label for="forgot-email" class="block text-sm font-marketing font-medium text-primary mb-1">
              Email address
            </label>
            <input
              id="forgot-email"
              v-model="forgotPasswordEmail"
              type="email"
              required
              class="appearance-none relative block w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black placeholder-gray-500 focus:z-10 sm:text-sm font-sans"
              placeholder="your@email.com"
            />
          </div>
        </div>
        <div class="flex justify-end gap-3">
          <button
            @click="showForgotPassword = false; forgotPasswordEmail = ''; forgotPasswordMessage = ''"
            class="px-4 py-2 text-sm font-marketing bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            @click="handleForgotPassword"
            :disabled="forgotPasswordLoading || !forgotPasswordEmail"
            class="px-4 py-2 text-sm font-marketing bg-accent text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ forgotPasswordLoading ? 'Sending...' : 'Send Reset Link' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { authService } from '../services/auth'

const pageContainer = ref(null)
const contentContainer = ref(null)
const email = ref('')
const password = ref('')
const error = ref('')
const sessionExpired = new URLSearchParams(window.location.search).get('session') === 'expired'
const loading = ref(false)
const showForgotPassword = ref(false)
const forgotPasswordEmail = ref('')
const forgotPasswordMessage = ref('')
const forgotPasswordLoading = ref(false)

const errorTitle = computed(() => {
  if (!error.value) return 'Login Error'
  const err = error.value.toLowerCase()
  if (err.includes('banned')) {
    return 'Account Banned'
  }
  if (err.includes('frozen')) {
    return 'Account Frozen'
  }
  return 'Login Error'
})

const shouldShowDialog = computed(() => {
  return error.value && (
    error.value.toLowerCase().includes('banned') ||
    error.value.toLowerCase().includes('frozen')
  )
})
const beeImage = '/logos/Icon Logo/No Background Black Logo Icon Cropped.png'

const bees = ref([
  { x: 100, y: 150, vx: 0.8, vy: 0.6, direction: 1 },
  { x: 300, y: 200, vx: -0.7, vy: 0.5, direction: -1 },
  { x: 500, y: 100, vx: 0.9, vy: -0.8, direction: 1 },
  { x: 700, y: 300, vx: -0.6, vy: -0.7, direction: -1 },
  { x: 900, y: 250, vx: 0.75, vy: 0.65, direction: 1 },
])

let animationFrame = null

const updateBees = () => {
  if (!pageContainer.value) {
    animationFrame = requestAnimationFrame(updateBees)
    return
  }

  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerLeft = containerRect.left
  const containerRight = containerRect.right
  const containerTop = containerRect.top
  const containerBottom = containerRect.bottom
  const containerWidth = containerRight - containerLeft
  const containerHeight = containerBottom - containerTop

  const cardCenterX = containerLeft + containerWidth / 2
  const cardCenterY = containerTop + containerHeight / 2
  const cardLeft = cardCenterX - 224
  const cardRight = cardCenterX + 224
  const cardTop = cardCenterY - 300
  const cardBottom = cardCenterY + 300

  bees.value.forEach(bee => {
    const prevVxSign = Math.sign(bee.vx)
    const prevX = bee.x
    const prevY = bee.y
    
    bee.x += bee.vx
    bee.y += bee.vy

    if (bee.x < 0) {
      bee.x = 0
      if (bee.vx < 0) {
        bee.vx = -bee.vx
        bee.x = Math.max(0, bee.x + bee.vx * 2)
      }
    } else if (bee.x > containerWidth - 48) {
      bee.x = containerWidth - 48
      if (bee.vx > 0) {
        bee.vx = -bee.vx
        bee.x = Math.min(containerWidth - 48, bee.x + bee.vx * 2)
      }
    }

    if (bee.y < 0) {
      bee.y = 0
      if (bee.vy < 0) {
        bee.vy = -bee.vy
        bee.y = Math.max(0, bee.y + bee.vy * 2)
      }
    } else if (bee.y > containerHeight - 48) {
      bee.y = containerHeight - 48
      if (bee.vy > 0) {
        bee.vy = -bee.vy
        bee.y = Math.min(containerHeight - 48, bee.y + bee.vy * 2)
      }
    }

    const beeCenterX = containerLeft + bee.x + 24
    const beeCenterY = containerTop + bee.y + 24

    const prevBeeCenterX = containerLeft + prevX + 24
    const prevBeeCenterY = containerTop + prevY + 24
    const wasInsideCard = prevBeeCenterX > cardLeft && prevBeeCenterX < cardRight && prevBeeCenterY > cardTop && prevBeeCenterY < cardBottom

    if (
      beeCenterX > cardLeft &&
      beeCenterX < cardRight &&
      beeCenterY > cardTop &&
      beeCenterY < cardBottom
    ) {
      if (!wasInsideCard) {
        const distanceFromLeft = beeCenterX - cardLeft
        const distanceFromRight = cardRight - beeCenterX
        const distanceFromTop = beeCenterY - cardTop
        const distanceFromBottom = cardBottom - beeCenterY

        const minDistance = Math.min(distanceFromLeft, distanceFromRight, distanceFromTop, distanceFromBottom)

        if (minDistance === distanceFromLeft || minDistance === distanceFromRight) {
          if ((minDistance === distanceFromLeft && bee.vx < 0) || (minDistance === distanceFromRight && bee.vx > 0)) {
            bee.vx = -bee.vx
            bee.x = prevX + bee.vx * 2
          }
        } else {
          if ((minDistance === distanceFromTop && bee.vy < 0) || (minDistance === distanceFromBottom && bee.vy > 0)) {
            bee.vy = -bee.vy
            bee.y = prevY + bee.vy * 2
          }
        }
      }
    }

    const newVxSign = Math.sign(bee.vx)
    if (prevVxSign !== 0 && newVxSign !== 0 && prevVxSign !== newVxSign) {
      bee.direction = newVxSign
    }
  })

  animationFrame = requestAnimationFrame(updateBees)
}

const handleResize = () => {
  if (!pageContainer.value) return
  
  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height
  
  bees.value.forEach(bee => {
    bee.x = Math.min(bee.x, containerWidth - 48)
    bee.y = Math.min(bee.y, containerHeight - 48)
  })
}

onMounted(() => {
  if (!pageContainer.value) return
  
  const containerRect = pageContainer.value.getBoundingClientRect()
  const containerWidth = containerRect.width
  const containerHeight = containerRect.height
  
  bees.value = [
    { x: containerWidth * 0.1, y: containerHeight * 0.2, vx: 0.8, vy: 0.6, direction: 1 },
    { x: containerWidth * 0.3, y: containerHeight * 0.3, vx: -0.7, vy: 0.5, direction: -1 },
    { x: containerWidth * 0.5, y: containerHeight * 0.15, vx: 0.9, vy: -0.8, direction: 1 },
    { x: containerWidth * 0.7, y: containerHeight * 0.4, vx: -0.6, vy: -0.7, direction: -1 },
    { x: containerWidth * 0.9, y: containerHeight * 0.3, vx: 0.75, vy: 0.65, direction: 1 },
  ]
  
  updateBees()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
  }
  window.removeEventListener('resize', handleResize)
})

const handleLogin = async () => {
  error.value = ''
  loading.value = true
  
  try {
    const result = await authService.login(email.value, password.value)

    if (result.success) {
      // Return the customer to where the session expired (e.g. /my-library after
      // a purchase), falling back to home. Only same-origin relative paths are
      // honored, so the param can't be used as an open redirect.
      const next = new URLSearchParams(window.location.search).get('next')
      window.location.href = next && next.startsWith('/') && !next.startsWith('//') ? next : '/'
    } else {
      error.value = typeof result.error === 'string' ? result.error : 'Login failed. Please try again.'
    }
  } catch (err) {
    error.value = 'An unexpected error occurred. Please try again.'
  } finally {
    loading.value = false
  }
}

const handleForgotPassword = async () => {
  forgotPasswordMessage.value = ''
  forgotPasswordLoading.value = true
  
  try {
    const result = await authService.forgotPassword(forgotPasswordEmail.value)
    
    if (result.success) {
      forgotPasswordMessage.value = result.data?.message || 'Password reset link has been sent to your email.'
      setTimeout(() => {
        showForgotPassword.value = false
        forgotPasswordEmail.value = ''
        forgotPasswordMessage.value = ''
      }, 3000)
    } else {
      forgotPasswordMessage.value = typeof result.error === 'string' ? result.error : 'Failed to send password reset email.'
    }
  } catch (err) {
    forgotPasswordMessage.value = 'An unexpected error occurred. Please try again.'
  } finally {
    forgotPasswordLoading.value = false
  }
}

</script>
